import { z } from "zod";
import { useTranslation } from "react-i18next";
import { IconHash, IconX } from "@tabler/icons-react";
import headerImage from "../../../assets/images/new-feed-promo-header.jpg";
import { Button } from "../../../components/ui/Button";
import { createLocalStore } from "../../../lib/store";

/**
 * Whether the reader has answered the card, on this device. Closing it and
 * either button are all an answer: the card shows once.
 */
export const useNewFeedPromoState = createLocalStore(
  "desktop-new-feed-promo",
  "unseen",
  z.enum(["unseen", "dismissed"])
);

/**
 * The card that introduces the practice feed (Figma "promo-modal-card"), in
 * the corner the Quick task otherwise takes. It floats over the feed and
 * blocks nothing: the reader can read on and answer it later.
 *
 * The page shows it while `useNewFeedPromoState` reads "unseen", and the
 * Quick task once it is answered.
 */
export function DesktopNewFeedPromo({
  practices,
  onDismiss,
}: {
  practices: ReadonlyArray<{ uid: string; name: string }>;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t("Your new feed")}
      className="fixed right-6 bottom-6 z-30 flex w-86.5 flex-col overflow-hidden rounded-2xl bg-ds-white font-dm-sans shadow-popover"
    >
      {/* The design's crop of the picture: its lower right, under the chips. */}
      <div
        aria-hidden
        className="relative h-45 w-full shrink-0 overflow-hidden bg-cover"
        style={{
          backgroundImage: `url(${headerImage})`,
          backgroundSize: "210% 160%",
          backgroundPosition: "82% 100%",
        }}
      >
        {/* Wider than the card on purpose: the chips run off its edge. */}
        <div className="absolute top-16 left-0 flex w-139 flex-wrap gap-2 px-4 pt-2.5">
          {practices.map((practice) => (
            <span
              key={practice.uid}
              className="flex h-7 items-center gap-1 rounded-lg bg-ds-slate-50 px-2.5 text-caption font-medium whitespace-nowrap text-ds-mint-950"
            >
              <IconHash size={14} stroke={1.25} />
              {practice.name}
            </span>
          ))}
        </div>
      </div>
      <button
        type="button"
        aria-label={t("Close")}
        onClick={onDismiss}
        className="absolute top-3 right-3 flex size-7 cursor-pointer items-center justify-center rounded-full border-none bg-ds-white/25 p-0 text-ds-slate-700"
      >
        <IconX size={20} stroke={1.5} />
      </button>
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <h2 className="m-0 text-desktop-heading font-bold text-ds-black">
            {t("Your new feed")}
          </h2>
          <p className="m-0 text-footnote leading-normal text-ds-slate-600">
            {t("Articles picked for your specialty and practices.")}{" "}
            {t("It adjusts as you read.")}
          </p>
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex-1"
            onClick={onDismiss}
          >
            {t("Later")}
          </Button>
          {/* The reader is already on the feed: trying it is reading on. */}
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={onDismiss}
          >
            {t("Try now")}
          </Button>
        </div>
      </div>
    </section>
  );
}
